import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  // Optional inline variant: renders a compact recoverable banner instead of the
  // full-screen fallback. Used around Suspense chunks so a chunk-load failure
  // doesn't blow away the rest of the app.
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.inline) {
        return (
          <div
            role="alert"
            style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
              padding: "12px 16px", margin: "16px", color: "#b91c1c", fontSize: 13,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            }}
          >
            <span>
              Couldn't load this section.
              {this.state.error?.message ? ` (${this.state.error.message})` : ""}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={this.handleReset}
                style={{ padding: "6px 14px", background: "#b91c1c", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{ padding: "6px 14px", background: "#fff", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >
                Reload
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="loading-screen">
          <div style={{ background: "#fff", borderRadius: 12, padding: "32px 28px", maxWidth: 440, textAlign: "center" }}>
            <h2 style={{ margin: "0 0 12px", color: "#b91c1c" }}>Something went wrong</h2>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 8px" }}>
              An unexpected error occurred. Please try again.
            </p>
            {this.state.error?.message && (
              <p style={{ color: "#9ca3af", fontSize: 12, margin: "0 0 20px", wordBreak: "break-word" }}>
                {this.state.error.message}
              </p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={this.handleReset}
                style={{ padding: "10px 24px", background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 }}
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{ padding: "10px 24px", background: "#e5e7eb", color: "#374151", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 }}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
